import { ECONOMY } from './economy-registry';
/**
 * Economy reason/source ids Block Jam spends against. Every one must start with
 * a prefix registered in `packages/economy/src/valid-prefixes.ts` — the wallet
 * throws on anything else.
 */
/** Credited when a level is cleared. */
export const LEVEL_COMPLETE_SOURCE_ID = 'blockjam_level_complete';
/**
 * Prefix for a power-up use (WP-35.17) — the id is appended in snake_case, e.g.
 * `blockjam_powerup_color_delete`. Per-power-up rather than one shared id so the
 * ledger can answer "what are players actually spending on" when the costs stop
 * being 0.
 */
export const POWERUP_REASON_PREFIX = 'blockjam_powerup_';
/**
 * Credited when a player watches a rewarded ad to double a level's payout
 * (Module 41, WP-41.11). Separate from {@link LEVEL_COMPLETE_SOURCE_ID} so the
 * ledger can tell an earned reward from a watched one — they are the same coins
 * and very different signals.
 */
export const REWARD_AD_DOUBLE_SOURCE_ID = 'blockjam_reward_ad_double';
/**
 * Credited when a player watches a rewarded ad from the minimal ad-only Shop
 * (CrazyGames only — `IGameContext.host.hasIap === false`). The AMOUNT comes
 * from the host (`IGameContext.host.shopCoinTopUp`); the reason id is the
 * game's, because the wallet's prefix rule keys ledger entries to whoever
 * spends them.
 *
 * Module 50 (WP-50.29) deleted its sibling `blockjam_reward_ad_topup`, which
 * fired from arming a power-up the player could not afford. That placement is
 * gone: coins buy power-ups, ads buy moves.
 */
export const REWARD_AD_SHOP_TOPUP_SOURCE_ID = 'blockjam_reward_ad_shop_topup';
/**
 * Module 96 (WP-96.5) — credited after an ad break (interstitial) the player sat
 * through: "+2 coins, thanks for watching". The AMOUNT comes from the host
 * (`IGameContext.host.adBreakThanks.coins`) and is always below the smallest
 * puzzle reward. Allowlisted server-side (1–10) by migration 0020, though only
 * the embed, which has no server wallet, credits it today.
 */
export const REWARD_AD_BREAK_THANKS_SOURCE_ID = 'blockjam_reward_ad_break_thanks';
/**
 * Module 137 — the one real-money product (Playgama Bridge `payments`): a
 * consumable granting `ECONOMY.rewards.iapCoinPack` coins. The embed shell
 * credits it after Playgama's verify (Economy_Rules rule 13), under
 * {@link IAP_COIN_PACK_SOURCE_ID}. No server row: the Bridge wallet is local.
 */
export const IAP_COIN_PACK_PRODUCT_ID = 'coins_1000';
export const IAP_COIN_PACK_SOURCE_ID = 'blockjam_iap_coins_1000';
/**
 * Module 126 (D14) — the Welcome gift for signing in (+ the Nimbus avatar).
 * Once ever per save (`welcomeGift` in the progress save). No server row: only
 * the Bridge build has sign-in, and its wallet is local.
 */
export const WELCOME_GIFT_SOURCE_ID = 'blockjam_welcome_gift';
export const WELCOME_GIFT_COINS = ECONOMY.rewards.welcomeGift;
/**
 * ⚠ Both rewarded-ad credits above are reachable **only** where the host
 * supplies `offerRewardedAd`, which today is the CrazyGames embed alone — and
 * the embed has no server-validated economy at all (no Supabase, by design).
 *
 * If a host with a real backend ever offers rewarded ads, the Supabase
 * `economy_apply` RPC allowlists `(kind, id, currency, amount)` exactly and
 * RAISES on anything it does not recognise. Migration 0012 carries a rule for
 * both, at the WP-50.25 reward set (5/10/20/40) and the 10-coin Shop row; migration
 * 0020 (Module 96) widened both credits to the range 1–60 for the reward curve, so
 * the trap this note describes is closed rather than merely described.
 */
/**
 * Module 67 (WP-67.C4) — the Daily Challenge's once-per-day clear reward.
 *
 * 20 coins (user decision 2026-09-05), ×10 to 200 by Module 97: out-pays a
 * typical level without dwarfing the first-clear curve. Once per UTC day, and the CLIENT gate
 * is advisory — migration 0015's wallet stamp (`lastBlockjamDailyDate`) is
 * what actually refuses a second same-day credit, exactly as
 * `daily_reward_claim` has always worked.
 */
export const DAILY_COMPLETE_SOURCE_ID = 'blockjam_daily_complete';
export const DAILY_REWARD_COINS = ECONOMY.rewards.dailyComplete;
/**
 * Module 101 (WP-101.4) — the once-ever reward for opening every explorable How
 * to Play topic (user, 2026-10-08). Locked power-ups never count, so a power-up
 * unlocked later becomes a NEW tile, never a second reward.
 *
 * The CLIENT gate (`howToRewarded` in the save) is advisory: migration 0023
 * allowlists exactly this id at exactly this amount, stamps `howtoExploredAt` in
 * the server wallet and refuses a second credit with `already_exists`, the
 * `system_starting_grant` pattern. Keep both literals equal to the migration's
 * branch — `howto-reward-migration.test.ts` reads the SQL to hold them there.
 */
export const HOWTO_EXPLORED_SOURCE_ID = 'blockjam_howto_explored';
export const HOWTO_EXPLORED_COINS = ECONOMY.rewards.howToExplored;
/**
 * Module 118 — daily reward, quests and coin sinks. Frozen at WP-118.1
 * (user, 2026-10-14: every proposed amount confirmed as planned). Every amount
 * and price is a multiple of 5 (Module 97's rule, held by
 * `bj-meta-economy.test.ts`). Migration 0024 (WP-118.3) allowlists exactly
 * these values; its parity test reads the SQL and compares it with these
 * literals, so change both together or neither.
 */
/**
 * Daily reward: a 7-day streak on the UTC day (the Daily Challenge's day
 * helper). Index 0 is day 1. A missed day resets to day 1 unless repaired
 * ({@link STREAK_REPAIR_PRICE}); after day 7 the cycle starts again at day 1.
 * Credited once per UTC day under {@link DAILY_LOGIN_SOURCE_ID}.
 */
export const DAILY_LOGIN_SOURCE_ID = 'blockjam_daily_login';
export const DAILY_LOGIN_SCHEDULE = ECONOMY.rewards.dailyLogin;
/** The streak day that also grants one free power-up use (the Module 112 ticket). */
export const DAILY_LOGIN_TICKET_DAY = ECONOMY.rewards.dailyLoginTicketDay;
/**
 * Quests: one permanent list of goals that teach the game. Each pays once ever,
 * credited under `QUEST_SOURCE_PREFIX + id` (e.g. `blockjam_quest_win_5`).
 * `target` is the count the progress bar fills to. How each one counts is
 * WP-118.2's (it hooks events the plugin already raises).
 */
export const QUEST_SOURCE_PREFIX = 'blockjam_quest_';
export const QUESTS = ECONOMY.rewards.quests;
/** `win_moves_spare` counts a win with at least this many moves left. */
export const QUEST_MOVES_SPARE = ECONOMY.rewards.questMovesSpare;
/**
 * Module 132 — Milestones (`meta/milestones.ts`). A tier is credited under
 * `MILESTONE_SOURCE_PREFIX + <id>_<tier>` (`blockjam_milestone_wins_silver`),
 * a ★ step after Platinum under `<id>_p<n>` (`blockjam_milestone_wins_p3`).
 */
export const MILESTONE_SOURCE_PREFIX = 'blockjam_milestone_';
export const MILESTONE_TIER_NAMES = ['bronze', 'silver', 'gold', 'platinum'];
/**
 * WP-132.11 — the player's title, from how many badges they hold (every tier
 * and ★ step on any milestone is one badge). It only ever goes up. After the
 * last title, one ★ per `PLAYER_TITLE_STEP` more badges ("Legend ★2").
 */
export const PLAYER_TITLES = [
    'newcomer',
    'explorer',
    'solver',
    'strategist',
    'expert',
    'master',
    'grandmaster',
    'legend',
];
/** Badges each title needs, same order. */
export const PLAYER_TITLE_AT = [0, 3, 8, 15, 25, 40, 60, 85];
export const PLAYER_TITLE_STEP = 15;
export const MILESTONES = ECONOMY.rewards.milestones.list;
export const MILESTONE_STEP_COINS = ECONOMY.rewards.milestones.endlessStep;
export const MILESTONE_MAX_STEP = ECONOMY.rewards.milestones.maxStep;
/**
 * Module 133 — Daily Missions (`meta/missions.ts`). A claim is credited under
 * `MISSION_SOURCE_PREFIX + <slot>_<coins>` (`blockjam_mission_2_100`): the
 * server pays each slot once per UTC day, at the amount its id names.
 */
export const MISSION_SOURCE_PREFIX = 'blockjam_mission_';
export const MISSION_POOL = ECONOMY.rewards.missions.pool;
export const MISSIONS_PER_DAY = 3;
export const MISSION_SET_BONUS_TICKETS = ECONOMY.rewards.missions.setBonusTickets;
/** The distinct mission amounts: each slot has one server row per amount. */
export const MISSION_COINS = [...new Set(MISSION_POOL.map((m) => m.coins))].sort((a, b) => a - b);
/**
 * Extra moves for coins in the out-of-moves popup, beside the moves ad (which
 * stays). The i-th buy in one attempt costs `EXTRA_MOVES_PRICES[i]` and adds
 * {@link EXTRA_MOVES_GRANT} moves. Offered in the Daily too, because the moves
 * ad is offered there today.
 *
 * Re-decided 2026-10-14 (user): coins buy MORE than the ad (+5 against the ad's
 * +3), so the coin sink works where ads are free; ad and coin rescues share one
 * cap of {@link RESCUES_PER_ATTEMPT} per attempt, any mix. Prices unchanged, so
 * migration 0024 (which checks the price, not the grant) still holds.
 */
export const EXTRA_MOVES_SOURCE_ID = 'blockjam_extra_moves';
export const EXTRA_MOVES_GRANT = ECONOMY.sinks.extraMoves.grant;
/** Ad and coin rescues together, per attempt (WP-118.7). */
export const RESCUES_PER_ATTEMPT = ECONOMY.caps.rescuesPerAttempt;
export const EXTRA_MOVES_PRICES = ECONOMY.sinks.extraMoves.prices;
/** Keep an N-day streak after missing exactly one UTC day. Once per missed day. */
export const STREAK_REPAIR_SOURCE_ID = 'blockjam_streak_repair';
export const STREAK_REPAIR_PRICE = ECONOMY.sinks.streakRepair;
/** One more try at a Daily Challenge that ended out of moves. Once per UTC day. */
export const DAILY_RETRY_SOURCE_ID = 'blockjam_daily_retry';
/**
 * Module 152 (WP-152.2) — a coin avatar's reason id, `blockjam_avatar_<id>`:
 * one exact server row per avatar (migration 0026), once per player per id.
 * The "New avatar!" view's big avatar carries it as its coin anchor, so the
 * spend flies to the avatar it bought (Module 110's debit rule).
 */
export const AVATAR_SOURCE_PREFIX = 'blockjam_avatar_';
export function avatarReasonId(id) {
    return `${AVATAR_SOURCE_PREFIX}${id}`;
}
export const DAILY_RETRY_PRICE = ECONOMY.sinks.dailyRetry;
/**
 * Module 123 — after a rewarded ad from the home row's Free Coins button, the
 * button waits this long before it offers another. Starts ONLY on a completed
 * (rewarded) ad. The Shop's own ad top-up has no cooldown (user decision
 * 2026-10-16: the Shop gets real IAP later).
 */
export const FREE_COINS_COOLDOWN_MS = ECONOMY.caps.freeCoinsCooldownMs;
